import { useEffect, useRef, useState } from "react";
import { Identifiable } from "@lifeat/shared/data-models/definition";
import { RecurringMasterTask, RecurringOverrideTask } from "@lifeat/shared/task-management/RecurringTaskTypes";
import "@lifeat/shared/task-management/TaskFilterTypes";
import { AnyTaskFilter } from "@lifeat/shared/task-management/TaskFilterTypes";
import {
    CombinedFilter,
    combineFilters,
    getInitialProjectIdForDraftTask,
} from "@lifeat/shared/task-management/TaskFilterUtil";
import {
    CreateNewTaskRequest,
    DuplicateTaskRequest,
} from "@lifeat/shared/task-management/TaskManagementFunctionContracts";
import { TaskItemType, TaskListType, userTaskDataCollection } from "@lifeat/shared/task-management/TaskManagementTypes";
import {
    isTaskDueToday,
    isTaskOverdue,
    withDuration,
    withScheduleDate,
    withScheduleTime,
    withoutDuration,
    withoutScheduleDate,
    withoutScheduleTime,
} from "@lifeat/shared/task-management/TaskManagementUtil";
import { ReorderTaskResponse } from "@lifeat/shared/task-management/TaskReorderingFunctionContracts";
import { TaskStatus } from "@lifeat/shared/task-management/TaskStatus";
import { MAXIMUM_SELECTED_TASK_FILTERS } from "@lifeat/shared/task-management/TaskTagConstants";
import { Tag } from "@lifeat/shared/task-management/TaskTagTypes";
import { calculateInitialTagIdForDraftTask } from "@lifeat/shared/task-management/TaskTagUtil";
import { withMutex } from "@lifeat/shared/util/AsyncUtil";
import { Result } from "@lifeat/shared/util/Result";
import { compareStringCaseInsensitive } from "@lifeat/shared/util/StringUtil";
import { useToastListContext } from "@contexts/ToastListContext";
import { useEvent } from "@hooks/useEvent";
import { useComputed, useSignal } from "@preact/signals-react";
import { logClientErrorMessage } from "@service/ClientLoggingService";
import { logError } from "@service/ConsoleLoggerService";
import { FirestoreService, QueryOptions } from "@service/FirestoreService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { FilterService } from "./FilterService";
import * as TaskListConstants from "./TaskListConstants";
import { TAG_FILTER_LIMIT_REACHED_BAD_DB_STATE_ERROR, TAG_FILTER_LIMIT_REACHED_ERROR } from "./TaskListConstants";
import { taskManagementOperationMutex } from "./TaskManagementGlobals";
import { TaskManagementService } from "./TaskManagementService";
import { ClientTaskItemType, ClientTaskListType, TaskFocusEvent, TaskServiceInterface } from "./TaskManagementTypes";
import { DraftTaskInitializationParams, buildDraftTask, getDraftTaskUID } from "./TaskManagementUtil";

export type UseTaskDataOptions = {
    uid?: string;
    filterService?: FilterService;
    selectedProjectId?: string | null;
};

type CombinedTaskListType = {
    dbTasks: ClientTaskListType;
    draftTasks: ClientTaskListType;
};

/**
 *
 * @param uid User ID to fetch task data for and perform operations on.
 * @param filterService Service to be used for filtering tasks in a specific context.
 * @param selectedProjectId ID of the project to filter tasks by. This is used in the project details page
 * when we want to filter by only one project
 */
export default function useTaskData({
    uid,
    filterService,
    selectedProjectId,
}: UseTaskDataOptions): TaskServiceInterface {
    const fs = FirestoreService();
    const { addToastNotification } = useToastListContext();

    const mutex = taskManagementOperationMutex;
    const taskListState$ = useSignal<CombinedTaskListType>({
        dbTasks: {},
        draftTasks: {},
    });
    const recurringMasters$ = useSignal<Record<string, RecurringMasterTask>>({});
    /** Grouped by master ID and then by index */
    const recurringOverrides$ = useSignal<Record<string, Record<number, RecurringOverrideTask>>>({});
    const taskItems$ = useComputed(() => ({ ...taskListState$.value.draftTasks, ...taskListState$.value.dbTasks }));

    // Note: this state keeps track of updates to the draft task title. This is to
    // prevent recent updates to the title from getting overwritten by the db task
    const draftTaskUpdates = useRef<null | ClientTaskListType>(null);

    // This is used to filter on a single project when viewing the project details page
    const selectedProjectFilter = selectedProjectId && ({ type: "project", project: selectedProjectId } as const);

    const [tags, setTags] = useState<Identifiable<Tag>[]>([]);
    const [defaultTagId, setDefaultTagId] = useState<string | null>(null);
    const [taskListFilters, setTaskListFilters] = useState<AnyTaskFilter[]>(
        selectedProjectFilter ? [selectedProjectFilter] : [],
    );

    const taskFocusEvent$ = useSignal<TaskFocusEvent | null>(null);

    const setNonProjectTaskListFilters = (filters: AnyTaskFilter[]) => {
        setTaskListFilters([...(selectedProjectFilter ? [selectedProjectFilter] : []), ...filters]);
    };

    // On task items update, remove any draft tasks that have been added to the database
    // so that there are no duplicate tasks in the dbTask list and draftTask list
    const handleTasksUpdate = useEvent((updatedDbTaskItems: TaskListType) => {
        const draftTasks = { ...taskListState$.value.draftTasks };

        // create map of db tasks by client id
        const dbTasksMappedByClientId: { [taskId: string]: { documentId: string } & TaskItemType } = {};
        Object.entries(updatedDbTaskItems).map(([key, task]: [string, TaskItemType]) => {
            dbTasksMappedByClientId[task.clientId] = {
                ...(updatedDbTaskItems[key] as TaskItemType),
                ...{ documentId: key },
            };
        });

        // Update task title and remove from draft task
        Object.entries(draftTasks).map(([clientTaskId, draftTask]: [string, ClientTaskItemType]) => {
            const taskDocumentId = dbTasksMappedByClientId[clientTaskId]?.documentId;

            if (taskDocumentId) {
                const dbTask = updatedDbTaskItems[taskDocumentId] as TaskItemType;
                // if the draft task and db task have different titles
                // update the title to the draft task's title
                const updatedDraftTask = draftTaskUpdates.current?.[clientTaskId];
                if (updatedDraftTask && updatedDraftTask.title !== dbTask.title) {
                    dbTask.title = updatedDraftTask.title;
                    TaskManagementService.updateTaskTitle(taskDocumentId, updatedDraftTask.title).catch(() => {
                        addToastNotification(
                            TaskListConstants.TITLE_UPDATE_ERROR_TITLE,
                            TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
                        );
                    });
                }
                // This is to maintain focus when a draft is replaced by a db task
                if (taskFocusEvent$.value?.type === "singular" && taskFocusEvent$.value.taskId === clientTaskId) {
                    taskFocusEvent$.value = {
                        type: "singular",
                        taskId: taskDocumentId,
                    };
                }
                delete draftTasks[clientTaskId];
            }
        });

        // Set isDraft to false for updatedDbTaskItems
        for (const id in updatedDbTaskItems) {
            updatedDbTaskItems[id] = { ...updatedDbTaskItems[id], isDraft: false } as ClientTaskItemType;
        }

        taskListState$.value = {
            ...taskListState$.value,
            dbTasks: updatedDbTaskItems as ClientTaskListType,
            draftTasks,
        };
        draftTaskUpdates.current = { ...draftTasks };
    });

    // Filters tasks based on the "today", "overdue", and project filters
    const filterTasks = (dbTaskItems: TaskListType, filters: CombinedFilter): TaskListType => {
        let filteredTasks = dbTaskItems;

        if (filters.overdue) {
            const filteredOverdueTasksEntries = Object.entries(filteredTasks).filter(
                ([taskId, task]) => task.schedulingDetails && isTaskOverdue(task.schedulingDetails, task.status),
            );
            filteredTasks = Object.fromEntries(filteredOverdueTasksEntries);
        }

        if (filters.today) {
            const filteredTodayTasksEntries = Object.entries(filteredTasks).filter(
                ([taskId, task]) => task.schedulingDetails && isTaskDueToday(task.schedulingDetails),
            );
            filteredTasks = Object.fromEntries(filteredTodayTasksEntries);
        }

        if (filters.unscheduled) {
            const filteredUnscheduledTaskEntries = Object.entries(filteredTasks).filter(
                ([taskId, task]) => !task.schedulingDetails?.scheduleDate && !task.schedulingDetails?.scheduleTime,
            );
            filteredTasks = Object.fromEntries(filteredUnscheduledTaskEntries);
        }

        if (filters.projectIds.length > 0) {
            const filteredTasksByProject: TaskListType = {};
            for (const taskId in filteredTasks) {
                const task = filteredTasks[taskId];
                if (task?.projectId && filters.projectIds.includes(task.projectId)) {
                    filteredTasksByProject[taskId] = task;
                }
            }
            filteredTasks = filteredTasksByProject;
        }

        return filteredTasks;
    };

    // Note: Only tag and status filtering is done on the firestore listener.
    // Date filtering (today and overdue filters) is done client side in `filterTasks()`
    // This is because all inequality filters in a compound query must be on the same field
    // Firestore doc: https://firebase.google.com/docs/firestore/query-data/queries#query_limitations
    const getQueryFilters = (filters: CombinedFilter) => {
        let filterOptions: QueryOptions["filters"] = [];

        if (filters.tagIds.length > MAXIMUM_SELECTED_TASK_FILTERS) {
            addToastNotification(
                TAG_FILTER_LIMIT_REACHED_BAD_DB_STATE_ERROR.title,
                TAG_FILTER_LIMIT_REACHED_BAD_DB_STATE_ERROR.description,
                TAG_FILTER_LIMIT_REACHED_BAD_DB_STATE_ERROR.duration,
            );
        }

        if (filters.tagIds.length > 0) {
            filterOptions.push({
                fieldPath: "tagId",
                opStr: "in",
                // Limit to 10 tags to avoid Firestore error with collection
                // listener for 'in' clause.
                value: filters.tagIds.slice(0, MAXIMUM_SELECTED_TASK_FILTERS),
            });
        }

        if (filters.status) {
            filterOptions.push({
                fieldPath: "status",
                opStr: "==",
                value: "OPEN",
            });
        }

        return filterOptions;
    };

    useEffect(() => {
        if (uid) {
            const node = userTaskDataCollection.at(uid).at("tasks").node;
            const combinedFilter = combineFilters(taskListFilters);
            const filters = getQueryFilters(combinedFilter);
            const unsub = fs.getCollectionListener(
                node,
                (payload) => {
                    if (payload.success && payload.value != null) {
                        const filteredTasks = filterTasks(payload.value, combinedFilter);
                        handleTasksUpdate(filteredTasks);
                    } else {
                        handleTasksUpdate({});
                    }
                },
                {
                    orderBy: { key: "orderRank", direction: "desc" },
                    filters: filters,
                },
            );
            return () => {
                unsub();
            };
        }
        return undefined;
    }, [uid, taskListFilters]);

    useEffect(() => {
        const node = userTaskDataCollection.at(uid).at("recurringMasters").node;
        const unsub = fs.getCollectionListener(node, (payload) => {
            if (payload.success && payload.value != null) {
                recurringMasters$.value = payload.value;
            } else {
                recurringMasters$.value = {};
            }
        });
        return () => {
            unsub();
        };
    }, [uid]);

    useEffect(() => {
        const node = userTaskDataCollection.at(uid).at("recurringOverrides").node;
        const unsub = fs.getCollectionListener(node, (payload) => {
            if (payload.success && payload.value != null) {
                const next: (typeof recurringOverrides$)["value"] = {};
                for (const override of Object.values(payload.value)) {
                    if (!next[override.masterId]) {
                        next[override.masterId] = {};
                    }
                    next[override.masterId][override.index] = override;
                }
                recurringOverrides$.value = next;
            } else {
                recurringOverrides$.value = {};
            }
        });
        return () => {
            unsub();
        };
    }, [uid]);

    useEffect(() => {
        if (uid && filterService) {
            const unsub = filterService.listenForFilters((payload) => {
                if (payload.success && payload.value != null) {
                    setNonProjectTaskListFilters(payload.value);
                } else {
                    setNonProjectTaskListFilters([]);
                }
            });
            return () => {
                unsub();
            };
        } else {
            return undefined;
        }
    }, [uid]);

    useEffect(() => {
        if (uid) {
            const unsub = fs.getDocumentListener(userTaskDataCollection.at(uid).node, (payload) => {
                if (payload.success && payload.value != null) {
                    setDefaultTagId(payload.value.defaultTagId || null);
                } else {
                    setDefaultTagId(null);
                }
            });
            return () => {
                unsub();
            };
        } else {
            return undefined;
        }
    }, [uid]);

    useEffect(() => {
        if (uid) {
            const unsub = fs.getCollectionListener(userTaskDataCollection.at(uid).at("tags").node, (payload) => {
                if (payload.success && payload.value != null) {
                    const sortedTags = Object.entries(payload.value)
                        .map(([key, value]) => ({ id: key, document: value }))
                        .sort((a, b) => compareStringCaseInsensitive(a.document.name ?? "", b.document.name ?? ""));
                    setTags(sortedTags);
                } else {
                    setTags([]);
                }
            });
            return () => {
                unsub();
            };
        } else {
            return undefined;
        }
    }, [uid]);

    const createDraftTask = (init: Omit<DraftTaskInitializationParams, "clientId" | "tagId">): string => {
        const taskId = getDraftTaskUID();
        const task = buildDraftTask({
            clientId: taskId,
            orderRank: init.orderRank,
            scheduleDate: init.scheduleDate,
            tagId: calculateInitialTagIdForDraftTask({
                defaultTagId,
                filters: taskListFilters,
                orderedTags: tags,
            }),
            projectId: getInitialProjectIdForDraftTask(taskListFilters),
        });

        const nextDraftEntries = Object.entries(taskListState$.value.draftTasks).filter(([_, item]) =>
            item.title?.trim(),
        ); // Remove existing empty draft tasks so that there are no multiple empty drafts.
        nextDraftEntries.push([taskId, task]);
        taskListState$.value = {
            ...taskListState$.value,
            draftTasks: { ...Object.fromEntries(nextDraftEntries) },
        };
        draftTaskUpdates.current = Object.fromEntries(nextDraftEntries);

        return taskId;
    };

    const deleteDraftTask = (taskId: string): void => {
        const currentDraftTasks = { ...taskListState$.value.draftTasks };
        delete currentDraftTasks[taskId];
        taskListState$.value = {
            ...taskListState$.value,
            draftTasks: currentDraftTasks,
        };
    };

    const updateDraftTaskTitle = (taskId: string, title: string): void => {
        const currentDraftTaskItems = { ...draftTaskUpdates.current };
        const taskItem = currentDraftTaskItems[taskId];
        if (taskItem != null && taskItem.title !== title) {
            taskItem.title = title;
            draftTaskUpdates.current = currentDraftTaskItems;
        }
    };

    const createTask = withMutex(mutex, async (payload: CreateNewTaskRequest): Promise<Result> => {
        if (payload.title.trim() === "") {
            return { success: false };
        }

        const data = await TaskManagementService.createNewTask(payload);

        if (data == null) {
            // delete associated draft task
            deleteDraftTask(payload.clientId);

            addToastNotification(
                TaskListConstants.TASK_CREATION_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        }

        return { success: true };
    });

    const duplicateTask = withMutex(mutex, async (payload: DuplicateTaskRequest): Promise<void> => {
        try {
            await callMonolith("duplicate-task", payload);
        } catch (error) {
            addToastNotification(
                TaskListConstants.TASK_DUPLICATION_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        }
    });

    const reorderTask = withMutex(
        mutex,
        async (
            taskId: string,
            localOrderRank: number,
            lowerBoundTaskId?: string,
            upperBoundTaskId?: string,
        ): Promise<void> => {
            const currentTaskItems = { ...taskListState$.value.dbTasks };
            const taskItem = currentTaskItems[taskId];
            if (taskItem != null && taskItem.orderRank !== localOrderRank) {
                taskItem.orderRank = localOrderRank;
                taskListState$.value = {
                    ...taskListState$.value,
                    dbTasks: currentTaskItems,
                };
            }
            await TaskManagementService.reorderTask(taskId, lowerBoundTaskId, upperBoundTaskId)
                .then((data: ReorderTaskResponse) => {
                    if (data != null && data.orderRank !== localOrderRank) {
                        logError(
                            `Task orderRank does not match between client [${localOrderRank}] and server [${data.orderRank}]`,
                        );
                        logClientErrorMessage(
                            `Task orderRank does not match between client [${localOrderRank}] and server [${data.orderRank}] for user [uid: ${uid}]`,
                        );
                    }
                })
                .catch(() => {
                    addToastNotification(
                        TaskListConstants.TASK_REORDER_ERROR_TITLE,
                        TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
                    );
                });
        },
    );

    const updateTaskTitle = withMutex(mutex, async (taskId: string, title: string): Promise<void> => {
        // TODO: use deep copy here since we are updating nested fields
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null && taskItem.title !== title) {
            taskItem.title = title;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskTitle(taskId, title).catch(() => {
            addToastNotification(TaskListConstants.TITLE_UPDATE_ERROR_TITLE, TaskListConstants.ERROR_TRY_AGAIN_MESSAGE);
        });
    });

    const updateTaskDescription = withMutex(mutex, async (taskId: string, description: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null && taskItem.description !== description) {
            taskItem.description = description;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskDescription(taskId, description).catch(() => {
            addToastNotification(
                TaskListConstants.DESCRIPTION_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskStatus = withMutex(mutex, async (taskId: string, status: TaskStatus): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null && taskItem.status !== status) {
            taskItem.status = status;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskStatus(taskId, status).catch(() => {
            addToastNotification(
                TaskListConstants.STATUS_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskTag = withMutex(mutex, async (taskId: string, tagId: string | null): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null && taskItem.tagId !== tagId) {
            taskItem.tagId = tagId;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskTag(taskId, tagId).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_TAG_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskProject = withMutex(mutex, async (taskId: string, projectId: string | null): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null && taskItem.projectId !== projectId) {
            taskItem.projectId = projectId;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskProject(taskId, projectId).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_PROJECT_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const deleteTask = withMutex(mutex, async (taskId: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            delete currentTaskItems[taskId];
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.deleteTasks([taskId]).catch(() => {
            addToastNotification(TaskListConstants.DELETE_TASK_ERROR_TITLE, TaskListConstants.ERROR_TRY_AGAIN_MESSAGE);
        });
    });

    const deleteTaskList = withMutex(mutex, async (taskIds: string[]): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        taskIds.forEach((taskId) => {
            delete currentTaskItems[taskId];
        });
        taskListState$.value = {
            ...taskListState$.value,
            dbTasks: currentTaskItems,
        };

        await TaskManagementService.deleteTasks(taskIds).catch(() => {
            addToastNotification(TaskListConstants.DELETE_TASK_ERROR_TITLE, TaskListConstants.ERROR_TRY_AGAIN_MESSAGE);
        });
    });

    const updateTaskDuration = withMutex(mutex, async (taskId: string, duration: number): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = withDuration(taskItem.schedulingDetails, duration);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskDuration(taskId, duration).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DURATION_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskDate = withMutex(mutex, async (taskId: string, date: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = withScheduleDate(taskItem.schedulingDetails, date);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskScheduleDate(taskId, date).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DATE_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskTime = withMutex(mutex, async (taskId: string, time: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = withScheduleTime(taskItem.schedulingDetails, time);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskScheduleTime(taskId, time).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_TIME_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskDueDate = withMutex(mutex, async (taskId: string, dueDate: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = { ...taskItem.schedulingDetails, dueDate: dueDate };
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskDueDate(taskId, dueDate).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DUE_DATE_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskScheduleDateAndOrderRank = withMutex(
        mutex,
        async (
            taskId: string,
            scheduleDate: string,
            localOrderRank: number,
            lowerBoundTaskId?: string,
            upperBoundTaskId?: string,
        ): Promise<void> => {
            const currentTaskItems = { ...taskListState$.value.dbTasks };
            const taskItem = currentTaskItems[taskId];
            if (taskItem != null) {
                taskItem.schedulingDetails = withScheduleDate(taskItem.schedulingDetails, scheduleDate);
                taskItem.orderRank = localOrderRank;
                taskListState$.value = {
                    ...taskListState$.value,
                    dbTasks: currentTaskItems,
                };
            }
            await TaskManagementService.updateTaskScheduleDateAndOrderRank(
                taskId,
                scheduleDate,
                lowerBoundTaskId,
                upperBoundTaskId,
            ).catch(() => {
                addToastNotification(
                    TaskListConstants.TASK_DND_UPDATE_ERROR_TITLE,
                    TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
                );
            });
        },
    );

    const removeTaskDueDate = withMutex(mutex, async (taskId: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = { ...taskItem.schedulingDetails, dueDate: null };
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.removeTaskDueDate(taskId).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DUE_DATE_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const removeTaskDuration = withMutex(mutex, async (taskId: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = withoutDuration(taskItem.schedulingDetails);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.removeTaskDuration(taskId).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DURATION_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const removeTaskTime = withMutex(mutex, async (taskId: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (taskItem != null) {
            taskItem.schedulingDetails = withoutScheduleTime(taskItem.schedulingDetails, timezone);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }
        await TaskManagementService.removeTaskScheduleTime(taskId, timezone).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_TIME_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const removeTaskDate = withMutex(mutex, async (taskId: string): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.schedulingDetails = withoutScheduleDate(taskItem.schedulingDetails);
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.removeTaskScheduleDate(taskId).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_DATE_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const updateTaskTimeSpent = withMutex(mutex, async (taskId: string, timeSpentMins: number): Promise<void> => {
        const currentTaskItems = { ...taskListState$.value.dbTasks };
        const taskItem = currentTaskItems[taskId];
        if (taskItem != null) {
            taskItem.timeSpentMins = timeSpentMins;
            taskListState$.value = {
                ...taskListState$.value,
                dbTasks: currentTaskItems,
            };
        }

        await TaskManagementService.updateTaskTimeSpent(taskId, timeSpentMins).catch(() => {
            addToastNotification(
                TaskListConstants.TASK_TIME_SPENT_UPDATE_ERROR_TITLE,
                TaskListConstants.ERROR_TRY_AGAIN_MESSAGE,
            );
        });
    });

    const toggleTagFilter = withMutex(mutex, async (tagId: string) => {
        // Get the list of filters that are not tag filters or are not the tag we are toggling.
        // This will include non-tag filters like status, date, etc.
        const filtersWithoutTheTag = taskListFilters.filter((filter) => filter.type !== "tag" || filter.tag !== tagId);
        const tagIsInFilters = filtersWithoutTheTag.length !== taskListFilters.length;
        const nextFilters = tagIsInFilters
            ? filtersWithoutTheTag
            : [...filtersWithoutTheTag, { type: "tag", tag: tagId } as const];

        const tagFilterList = taskListFilters.filter((filter) => filter.type === "tag");

        // Cannot filter on more than 10 tags at a time due to limitation with Firestore
        // collection listener query.
        if (!tagIsInFilters && tagFilterList.length + 1 > MAXIMUM_SELECTED_TASK_FILTERS) {
            addToastNotification(
                TAG_FILTER_LIMIT_REACHED_ERROR.title,
                TAG_FILTER_LIMIT_REACHED_ERROR.description,
                TAG_FILTER_LIMIT_REACHED_ERROR.duration,
            );
        } else {
            setTaskListFilters(nextFilters);
            await filterService?.setFilters(nextFilters);
        }
    });

    const clearTagFilters = withMutex(mutex, async () => {
        const nextFilters = taskListFilters.filter((filter) => filter.type !== "tag");
        setTaskListFilters(nextFilters);
        await filterService?.setFilters(nextFilters);
    });

    const toggleProjectFilter = withMutex(mutex, async (projectId: string) => {
        // Get the list of filters that are not project filters or are not the project we are toggling.
        const filtersWithoutTheProject = taskListFilters.filter(
            (filter) => filter.type !== "project" || filter.project !== projectId,
        );
        const projectIsInFilters = filtersWithoutTheProject.length !== taskListFilters.length;
        const nextFilters = projectIsInFilters
            ? filtersWithoutTheProject
            : [...filtersWithoutTheProject, { type: "project", project: projectId } as const];

        setTaskListFilters(nextFilters);
        await filterService?.setFilters(nextFilters);
    });

    const clearProjectFilters = withMutex(mutex, async () => {
        const nextFilters = taskListFilters.filter((filter) => filter.type !== "project");
        setTaskListFilters(nextFilters);
        await filterService?.setFilters(nextFilters);
    });

    const toggleOpenStatusFilter = withMutex(mutex, async () => {
        const otherFilters = taskListFilters.filter((filter) => filter.type !== "status" || filter.status !== "OPEN");
        // We check to see if the filter is already included in the list of filters.
        // If it is, we remove it (toggling off). If not, we add it (toggling on).
        const statusFilterIsIncluded = otherFilters.length !== taskListFilters.length;
        const updatedFilters = statusFilterIsIncluded
            ? otherFilters
            : [...otherFilters, { type: "status", status: "OPEN" } as const];
        setTaskListFilters(updatedFilters);
        await filterService?.setFilters(updatedFilters);
    });

    const toggleTodayFilter = withMutex(mutex, async () => {
        const otherFilters = taskListFilters.filter((filter) => filter.type !== "today");
        // We check to see if the filter is already included in the list of filters.
        // If it is, we remove it (toggling off). If not, we add it (toggling on).
        const todayFilterIsIncluded = otherFilters.length !== taskListFilters.length;
        let updatedFilters = todayFilterIsIncluded ? otherFilters : [...otherFilters, { type: "today" } as const];

        // If we are toggling the today filter on and the overdue or unscheduled filter is included, we remove it
        // Because we cant filter on today and overdue or unscheduled at the same time
        if (!todayFilterIsIncluded) {
            updatedFilters = updatedFilters.filter(
                (filter) => filter.type !== "overdue" && filter.type !== "unscheduled",
            );
        }

        setTaskListFilters(updatedFilters);
        await filterService?.setFilters(updatedFilters);
    });

    const toggleOverdueFilter = withMutex(mutex, async () => {
        const otherFilters = taskListFilters.filter((filter) => filter.type !== "overdue");
        // We check to see if the filter is already included in the list of filters.
        // If it is, we remove it (toggling off). If not, we add it (toggling on).
        const overdueFilterIsIncluded = otherFilters.length !== taskListFilters.length;
        let updatedFilters = overdueFilterIsIncluded ? otherFilters : [...otherFilters, { type: "overdue" } as const];

        // If we are toggling the overdue filter on and the today or unscheduled filter is included, we remove it
        // Because we cant filter on today and overdue or unscheduled at the same time
        if (!overdueFilterIsIncluded) {
            updatedFilters = updatedFilters.filter(
                (filter) => filter.type !== "today" && filter.type !== "unscheduled",
            );
        }

        setTaskListFilters(updatedFilters);
        await filterService?.setFilters(updatedFilters);
    });

    const toggleUnscheduledFilter = withMutex(mutex, async () => {
        const otherFilters = taskListFilters.filter((filter) => filter.type !== "unscheduled");
        // We check to see if the filter is already included in the list of filters.
        // If it is, we remove it (toggling off). If not, we add it (toggling on).
        const unscheduledFilterIsIncluded = otherFilters.length !== taskListFilters.length;
        let updatedFilters = unscheduledFilterIsIncluded
            ? otherFilters
            : [...otherFilters, { type: "unscheduled" } as const];

        // If we are toggling the unscheduled filter on and the today or overdue filter is included, we remove it
        // Because we cant filter on today and overdue or unscheduled at the same time
        if (!unscheduledFilterIsIncluded) {
            updatedFilters = updatedFilters.filter((filter) => filter.type !== "today" && filter.type !== "overdue");
        }

        setTaskListFilters(updatedFilters);
        await filterService?.setFilters(updatedFilters);
    });

    return {
        taskItems$,
        recurringMasters$,
        recurringOverrides$,
        tags,
        filters: taskListFilters,
        defaultTagId,
        createDraftTask,
        deleteDraftTask,
        updateDraftTaskTitle,
        createTask,
        duplicateTask,
        reorderTask,
        updateTaskTitle,
        updateTaskDescription,
        updateTaskStatus,
        updateTaskTag,
        updateTaskProject,
        deleteTask,
        deleteTaskList,
        updateTaskDuration,
        updateTaskDate,
        updateTaskTime,
        updateTaskDueDate,
        updateTaskScheduleDateAndOrderRank,
        removeTaskDueDate,
        removeTaskDuration,
        removeTaskTime,
        removeTaskDate,
        updateTaskTimeSpent: updateTaskTimeSpent,
        toggleTagFilter,
        clearTagFilters,
        toggleProjectFilter,
        clearProjectFilters,
        toggleOpenStatusFilter,
        toggleTodayFilter,
        toggleOverdueFilter,
        toggleUnscheduledFilter,
        taskFocusEvent$,
    };
}
